import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import App from './App'
import { AuthGate } from './core/auth/AuthGate'
const WorkspaceRouteHost = lazy(() => import('./modules/mentorship/workspace/WorkspaceRouteHost'))

// Public, unauthenticated VPAT-like statement. Mounted outside
// <AuthGate> so anyone — search engines, users without a session,
// users hitting a barrier in the auth flow itself — can reach it.
const AccessibilityPage = lazy(() => import('./modules/marketing/AccessibilityPage'))
import { UserProvider } from './core/auth/UserProvider'
import { ToastProvider } from './core/ui/toast/ToastProvider'
import { ErrorBoundary } from './core/errors/ErrorBoundary'
import OfflineBanner from './core/errors/OfflineBanner'
import { ThemeProvider } from './core/theme/ThemeProvider'
import { TelemetryProvider } from './core/telemetry/TelemetryProvider'
import { defaultTelemetrySink } from './core/telemetry/defaultTelemetrySink'
import { initAnalytics } from './analytics'
import { reportWebVitals } from './core/perf/webVitals'
// Slice 91 (GAP-MIGRATION-SAFETY-LEGACY) — bookmarks to retired SPA
// paths (Today / Inbox / At risk / Disputes / Projects / Clients /
// Curation / Trends / Engagement / Submissions / Recommendations /
// Progression / Mentee-home) must redirect to the canonical surface
// AND inform the user the page moved. The SSOT table lives in
// `legacyRedirects.ts`; the wrapper component below maps each row to
// a <Route> that <Navigate replace>s + sets the banner handshake.
import { LEGACY_REDIRECTS, resolveBannerText } from './core/navigation/legacyRedirects'
import LegacyRedirect from './core/navigation/LegacyRedirect'
import LegacyRedirectBanner from './core/navigation/LegacyRedirectBanner'
// Redesign tokens + chrome primitives — loaded FIRST so existing index.css
// overrides still win the cascade while new .ui-* classes from ui.css are
// available to new modules.
import './styles/tokens.css'
import './styles/ui.css'
import './index.css'
import './App.css'
// SurfaceHeader — component styles + cross-surface normaliser in one
// file. Normalises every module header to match the SSOT spec:
// 64px height, 16px padding, bg-canvas, border-bottom, display-font
// 18px title. Stays critical because the header IS the LCP element on
// most surfaces.
import './core/layout/SurfaceHeader.css'
// Typography SSOT — every font-family / size / weight mapped to the
// redesign token scale (--text-xs..5xl, --font-sans/display/mono).
// Critical for first paint readability.
import './styles/typography.css'
// Typography primitives — <Title>, <Text>, <Link> component styles.
// Critical because primitive classes are used by every header / title.
import './core/ui/typography/Typography.css'
// Surface-bg SSOT — forces every module's main-pane root class to
// render on var(--bg-app). Critical because deferring causes a brief
// flash of the wrong background colour on initial paint (FOUB).
import './styles/surface-bg.css'
// Scroll-fade affordance — mask-image gradient applied to scrollable
// containers. Critical because the fade is visible on the initial
// scroll position of the rail nav + main pane.
import './core/ui/scrollFade.css'

// ── Deferred non-critical stylesheets ──────────────────────────────
//
// The three sheets below land AFTER the React mount, in the first
// idle window the browser hands us. They are non-LCP-critical:
//   - cross-browser-fixes.css: polyfills + quirks for non-modern
//     browsers; never affects first paint on modern Chrome / Safari /
//     Firefox / Edge that ship to >95% of traffic.
//   - focus.css: only fires on `:focus-visible` (keyboard focus).
//     First paint has no focused element, so the rules don't apply.
//   - animations.css: `prefers-reduced-motion` umbrella; only matters
//     after the first user-triggered animation, AND the OS-level
//     preference is honoured by the browser regardless.
// Vite chunks each dynamic-import target into its own CSS file the
// browser fetches AFTER the entry bundle, off the LCP critical path.
function loadNonCriticalStyles(): void {
  void import('./styles/cross-browser-fixes.css');
  void import('./styles/focus.css');
  void import('./styles/animations.css');
}
type IdleCallback = (cb: () => void) => void;
const scheduleIdle: IdleCallback = typeof window !== 'undefined' && 'requestIdleCallback' in window
  ? (cb) => (window as Window & { requestIdleCallback: (handler: () => void, opts?: { timeout: number }) => number }).requestIdleCallback(cb, { timeout: 2000 })
  : (cb) => { setTimeout(cb, 250); };
scheduleIdle(loadNonCriticalStyles);

// Boot analytics (loads GA4 only if consent was previously granted)
initAnalytics();

// Pre-React theme application — avoids a flash of incorrect theme on
// boot. ThemeProvider picks up the same sources once React mounts.
//
// Storage precedence:
//   1. `chiefy_theme` cookie (parent-domain scoped — survives the BE
//      auth ↔ SPA subdomain handoff per CANONICAL_PALETTE.md).
//   2. `chiefy.theme` localStorage (same-origin fallback for legacy
//      installs before the cookie bridge landed).
//
// Same logic exists in three places by intent: this script, the
// Next.js layout.tsx pre-render <script>, and the Django
// templates/base.html boot script. Each surface needs its own
// inline copy because they run before any framework code; consolidating
// them into a shared module would mean an extra round-trip and reintroduce
// the flash this script exists to prevent.
(function bootTheme() {
  const cookieMatch = document.cookie.match(/(?:^|; )chiefy_theme=([^;]+)/);
  const cookieValue = cookieMatch ? decodeURIComponent(cookieMatch[1] ?? '') : null;
  const stored = cookieValue ?? localStorage.getItem('chiefy.theme');
  const choice = (stored === 'light' || stored === 'dark' || stored === 'system') ? stored : 'system';
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
  const resolved = choice === 'system' ? (prefersDark ? 'dark' : 'light') : choice;
  document.documentElement.setAttribute('data-theme', resolved);
})();

// iOS Safari: add touchstart listener so the browser treats elements as "touchable"
// and fires click events immediately without waiting for double-tap
document.addEventListener('touchstart', () => {}, { passive: true });

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <OfflineBanner />
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            {/* Slice 91 — banner mounted at the BrowserRouter level
                (above Routes) so it survives the redirect commit and
                reads the sessionStorage flag the <LegacyRedirect>
                wrapper just wrote. Rendering is gated on the flag's
                presence; absent flag ⇒ banner returns null and adds
                zero chrome to canonical surfaces. */}
            <LegacyRedirectBanner />
            <Routes>
              {/* Public marketing/legal pages — bypass <AuthGate> so
                  they remain reachable without a session. */}
              <Route
                path="/accessibility"
                element={
                  <Suspense fallback={null}>
                    <AccessibilityPage />
                  </Suspense>
                }
              />
              {/* Per-mentee workspace focus surface — bypasses the tab-switching
                  App shell. AuthGate, UserProvider, and TelemetryProvider are
                  still required so auth + context work identically on this
                  route. Placed BEFORE the catch-all so the router matches it
                  specifically rather than falling through to <App>. */}
              <Route
                path="/mentorship/:menteeId"
                element={
                  <Suspense fallback={null}>
                    <AuthGate>
                      <UserProvider>
                        <TelemetryProvider onTrack={defaultTelemetrySink}>
                          <WorkspaceRouteHost />
                        </TelemetryProvider>
                      </UserProvider>
                    </AuthGate>
                  </Suspense>
                }
              />
              {/* Slice 91 (GAP-MIGRATION-SAFETY-LEGACY) — legacy
                  bookmark redirects. One <Route> per `LEGACY_REDIRECTS`
                  SSOT row. Placed BEFORE the SPA catch-all so the
                  router matches the retired path specifically rather
                  than falling through to <App>'s tab-switching shell.
                  The <LegacyRedirect> wrapper writes the banner
                  handshake to sessionStorage, then <Navigate replace>s
                  to the canonical destination — the URL is rewritten
                  atomically, no double-history-entry. Loop-safety is
                  enforced at module load by `assertNoRedirectCycles`. */}
              {Object.values(LEGACY_REDIRECTS).map((row) => (
                <Route
                  key={row.legacyPath}
                  path={row.legacyPath}
                  element={
                    <LegacyRedirect
                      to={row.newPath}
                      from={row.legacyPath}
                      bannerText={resolveBannerText(row)}
                    />
                  }
                />
              ))}
              {/* Catch-all → the authenticated SPA shell. */}
              <Route
                path="*"
                element={
                  <AuthGate>
                    <UserProvider>
                      <TelemetryProvider onTrack={defaultTelemetrySink}>
                        <App />
                      </TelemetryProvider>
                    </UserProvider>
                  </AuthGate>
                }
              />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)

// Wire Web Vitals (LCP/CLS/INP/TTFB/FCP) → analytics + captureError. The
// shim is idempotent; see src/core/perf/webVitals.ts for thresholds and the
// SSOT for the perf-budget reporter.
reportWebVitals();
